import { CartFlyout, CartProduct } from '@westwing/ui-kit/ContentFlyout/CartFlyout';
import { theme } from '@westwing/ui-kit/Theme';
import React, { useEffect, useRef } from 'react';
import { CartProductItem } from '@westwing/ui-kit/ContentFlyout/CartFlyout/types';
import CartFlyoutContainer from '../CartFlyoutContainer';
import { ClubLink } from '../types/clubLinksTypes';
import { useClubContext } from '../hooks/ClubContext';
import { useOneCartContext } from '../hooks/OneCartContext';
import { getOneCartLink, getOpenClubOneCartLink } from '../clubLinks';
import { addUrlParam } from '../../utils/urls';
import useIsVisible from '../hooks/useIsVisible';
import { useHeaderContext } from 'Components/HeaderContext';

interface CartFlyoutWrapperProps {
    amountOfItems: number;
    remainingTime: number;
    products: CartProductItem[];
    remindAt: number;
    isVisible: boolean;
    totalPrice: string;
    isDeliveryUpdateVisible: boolean;
    isCartExpired?: boolean;
    clubStaticHost: string;
    setFlyoutIntersecting?: (isIntersecting: boolean) => void;
}

const CartFlyoutWrapper = ({
    amountOfItems,
    remainingTime,
    products = [],
    remindAt,
    isVisible,
    totalPrice,
    isDeliveryUpdateVisible,
    isCartExpired = false,
    clubStaticHost,
    setFlyoutIntersecting,
}: CartFlyoutWrapperProps): JSX.Element => {
    const { t } = useHeaderContext();
    const { links, isOpenClubEnabled } = useClubContext();
    const { isOneCartEnabled } = useOneCartContext();
    const flyoutCartRef = useRef<HTMLDivElement>(null);
    const isFlyoutCartIntersecting = useIsVisible(flyoutCartRef);

    useEffect(
        () => setFlyoutIntersecting?.(isFlyoutCartIntersecting),
        [isFlyoutCartIntersecting, setFlyoutIntersecting]
    );

    const timer = isOneCartEnabled
        ? undefined
        : {
              remainingTime,
              startsTimeoutAt: remindAt,
              reservedForLabel: t('OwwHeader_Reserved for you for'),
              minutesLabel: t('OwwHeader_minutes'),
              expiredTimeReservedForLabel: t('OwwHeader_reservation'),
              expiredTimeLabel: t('OwwHeader_expired'),
              infoText: t(
                  'OwwHeader_The items in your cart are reserved for a limited time only. After that they will be made available for all customers again'
              ),
          };
    const oneCartLink = isOpenClubEnabled ? getOpenClubOneCartLink() : getOneCartLink();
    const cartUrl = isOneCartEnabled ? oneCartLink : links[ClubLink.CART].path;

    const generateImageUrl = (product: CartProductItem) => {
        const imageOptionsObj = {
            src: '',
        };
        if (product?.origin === 'club') {
            imageOptionsObj.src = `${clubStaticHost}${product.image}`;
        } else {
            imageOptionsObj.src = product.image;
        }
        return imageOptionsObj;
    };

    const generateProductList = () => {
        const productList = products.map(product => {
            const isClubItem = product.origin === 'club';
            return (
                <CartProduct
                    originalPrice={product.originalPrice}
                    origin={product.origin}
                    key={`${product.name}`}
                    quantityLabel={t('OwwHeader_Quantity')}
                    quantity={product.quantity}
                    price={product.price}
                    brand={product.brand}
                    name={product.name}
                    dataTestId="oww-header-cart-flyout-product"
                    quantityDataTestId="oww-header-cart-flyout-product-quantity"
                    productLinkProps={{ href: product.productUrl }}
                    productImageComponent="img"
                    productImageProps={isOneCartEnabled ? generateImageUrl(product) : { src: product.image }}
                    productLinkComponent="a"
                    isCartExpired={isCartExpired}
                    isClubItem={isClubItem}
                    discountColor={theme.colors.red100}
                />
            );
        });

        if (isOneCartEnabled) {
            return productList.reverse();
        }

        return productList;
    };

    return (
        <CartFlyoutContainer ref={flyoutCartRef} data-testid="oww-header-cart-flyout-container" $isVisible={isVisible}>
            <CartFlyout
                amountOfItems={amountOfItems}
                maxAmountOfItems={2}
                cartUrl={cartUrl}
                subtotal={totalPrice ?? ''}
                totalSumLabel={t('OwwHeader_Total sum')}
                dataTestId="oww-header-cart-flyout"
                bottomDataTestId="oww-header-cart-flyout-bottom"
                seeProductsLabel={t('OwwHeader_All products in your cart')}
                allProductsLinkComponent="a"
                allProductsLinkProps={{ href: cartUrl }}
                goToCartLabel={remainingTime > 0 ? t('OwwHeader_To Cart') : t('OwwHeader_Restore product')}
                goToCartProps={{ href: `${remainingTime > 0 ? cartUrl : addUrlParam(cartUrl, 'restore_cart', '1')}` }}
                productQuantityLabel={t('OwwHeader_Quantity')}
                isDeliveryUpdateVisible={isDeliveryUpdateVisible}
                deliveryUpdateText={`${t('OwwHeader_The delivery time of this product has changed.')} ${t(
                    'OwwHeader_You will see them in the shopping cart.'
                )}`}
                timer={timer}
            >
                {generateProductList()}
            </CartFlyout>
        </CartFlyoutContainer>
    );
};

export default CartFlyoutWrapper;
